import { isEmpty } from 'lodash'
import React from 'react'

const OddsHighlighterContext = React.createContext()

export const ODDS_HIGHLIGHTED_STATUS = {
  SELECTED: 1,
  PLACED: 2,
  EDITING: 4,
  LOADING: 8,
  FAILED: 16,
  SUCCEEDED: 32
}

const reducer = (state, action) => {
  const outcomes = action.payload

  if (action.type === 'update') {
    const updatedOutcomes = Object.keys(outcomes).reduce(
      (acc, outcomeId) => {
        acc[outcomeId] = state[outcomeId]
          ? state[outcomeId] | outcomes[outcomeId]
          : outcomes[outcomeId]

        return acc
      },
      {}
    )

    return {
      ...state,
      ...updatedOutcomes
    }
  }

  if (action.type === 'remove') {
    const highlightedOutcomeIds = Object.keys(state)

    return highlightedOutcomeIds.reduce(
      (acc, outcomeId) => {
        if (outcomes[outcomeId]) {
          acc[outcomeId] = state[outcomeId] ^ outcomes[outcomeId]
        } else {
          acc[outcomeId] = state[outcomeId]
        }

        return acc
      },
      {}
    )
  }

  return state
}

const initialState = {}

export function OddsHighlighter (props) {
  const value = React.useReducer(reducer, initialState)

  return (
    <OddsHighlighterContext.Provider value={value}>
      {props.children}
    </OddsHighlighterContext.Provider>
  )
}

export function useHighlightedOutcome (outcomeId) {
  const [state] = React.useContext(OddsHighlighterContext)
  const oddsHighlightedStatus = state[outcomeId]

  return React.useMemo(
    () => ({
      selected: (oddsHighlightedStatus & ODDS_HIGHLIGHTED_STATUS.SELECTED) > 0,
      editing: (oddsHighlightedStatus & ODDS_HIGHLIGHTED_STATUS.EDITING) > 0,
      placed: (oddsHighlightedStatus & ODDS_HIGHLIGHTED_STATUS.PLACED) > 0,
      loading: (oddsHighlightedStatus & ODDS_HIGHLIGHTED_STATUS.LOADING) > 0,
      failed: (oddsHighlightedStatus & ODDS_HIGHLIGHTED_STATUS.FAILED) > 0,
      succeeded: (oddsHighlightedStatus & ODDS_HIGHLIGHTED_STATUS.SUCCEEDED) > 0
    }),
    [oddsHighlightedStatus]
  )
}

// Highlighter implementations

export function useBetsOddsHighlight (bets, status = ODDS_HIGHLIGHTED_STATUS.SELECTED) {
  const [state, dispatcher] = React.useContext(OddsHighlighterContext)

  const betsHash = React.useMemo(
    () => bets.map(bet => bet.outcomeId).toString(),
    [bets]
  )

  React.useEffect(
    () => {
      const payload = bets.reduce(
        (acc, bet) => {
          acc[bet.outcomeId] = status
          return acc
        },
        {}
      )

      dispatcher({
        type: 'update',
        payload
      })

      return () => {
        dispatcher({
          type: 'remove',
          payload
        })
      }
    },
    [betsHash]
  )

  return state
}

export const useReportsOddsHighlight = (reports, status = ODDS_HIGHLIGHTED_STATUS.PLACED) => {
  const [state, dispatcher] = React.useContext(OddsHighlighterContext)

  const reportsHash = React.useMemo(
    () => reports.map(report => report.betId).toString(),
    [reports]
  )

  React.useEffect(
    () => {
      const payload = reports.reduce(
        (acc, report) => {
          return {
            ...acc,
            ...report.outcomes.reduce(
              (acc, reportOutcome) => {
                const outcomeId = reportOutcome.outcomeId
                acc[outcomeId] = status
                return acc
              },
              {}
            )
          }
        },
        {}
      )

      dispatcher({
        type: 'update',
        payload
      })

      return () => {
        dispatcher({
          type: 'remove',
          payload
        })
      }
    },
    [reportsHash]
  )

  return state
}

export const useErrorsOddsHighlight = (errors, status = ODDS_HIGHLIGHTED_STATUS.FAILED) => {
  const [state, dispatcher] = React.useContext(OddsHighlighterContext)

  const errorsHash = React.useMemo(
    () => Object.values(errors).map(error => error.requestId || 0).toString(),
    [errors]
  )

  React.useEffect(
    () => {
      const payload = Object.values(errors).reduce(
        (acc, error) => {
          if (isEmpty(error.betDto) || isEmpty(error.betDto.outcomes)) {
            return acc
          }

          return {
            ...acc,
            ...error.betDto.outcomes.reduce(
              (acc, outcome) => {
                const outcomeId = outcome.outcomeId
                acc[outcomeId] = status
                return acc
              },
              {}
            )
          }
        },
        {}
      )

      dispatcher({
        type: 'update',
        payload
      })

      return () => {
        dispatcher({
          type: 'remove',
          payload
        })
      }
    },
    [errorsHash]
  )

  return state
}
